import React, { useRef, useState } from 'react';

import mapbox from 'mapbox-gl';

import { WEB_GL_DISABLED_WARNING_MESSAGE } from '../../../constants/constants';
import { useGeolocation } from '../../../contexts/GeolocationContext/GeolocationContext';
import { useMapLayers } from '../../../contexts/MapLayersContext';
import { MapboxExpectedError, useNewMap } from '../../../helpers/hooks/useNewMap';
import { useIsTabletScreenWidthOrThinner } from '../../../helpers/hooks/useScreenWidth';
import { useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { ZoomNotification } from '../../../views/Explore/components/ZoomNotification/ZoomNotification';
import { useExplore } from '../../../views/Explore/context/ExploreContext';
import {
  getMonthArrayFromUrl,
  getSpeciesIdArrayFromUrl,
} from '../../../views/Explore/context/helpers';
import { useUpdateExploreUrlFromUserInteraction } from '../../../views/Explore/hooks/useUpdateExploreUrlFromUserInteraction';
import { ErrorBanner } from '../../ErrorBanner/ErrorBanner';
import NextImage from 'next/image';
import MapboxErrorBoundary from '../../MapboxErrorBoundary/MapboxErrorBoundary';
import { MobileSearch } from '../../SearchBoxMapContext/components/MobileSearch';
import { SearchBoxMapContext } from '../../SearchBoxMapContext/SearchBoxMapContext';
import crosshair from '../assets/crosshair.png';
import { getInitialZoomLevel } from '../helpers/getInitialZoomLevel';
import { handleArtificialReefClick } from '../helpers/handleArtificialReefClick';
import { handleCatchIconClick } from '../helpers/handleCatchIconClick';
import { handlePointOfInterestClick } from '../helpers/handlePointOfInterestClick';
import { handleWaterIconClick } from '../helpers/handleWaterIconClick';
import { handleWaypointIconClick } from '../helpers/handleWaypointIconClick';
import { useMapResize } from '../hooks/useMapResize';
import { ARTIFICIAL_REEFS_LAYER } from '../mapData/mapLayers/artificialReefs';
import { CATCH_POSITIONS_LAYER } from '../mapData/mapLayers/catchPositions';
import {
  EMPTY_FISHING_WATERS_LAYER,
  FISHING_WATERS_LAYER,
  FORBIDDEN_FISHING_WATERS_LAYER,
} from '../mapData/mapLayers/fishingWaters';
import { POINTS_OF_INTEREST_LAYERS } from '../mapData/mapLayers/pointsOfInterest';
import { WAYPOINTS_LAYERS } from '../mapData/mapLayers/waypoints';
import { FishbrainLayer } from '../types/types';

import { MapIconClickHandler } from './MapIconClickHandler';
import { DEFAULT_COORDINATES } from '../constants/constants';

const MainMap = () => {
  useMapResize();

  const { isPremium } = useUserInfo();

  const { exploreQueryParams } = useExplore();
  const { latitude, longitude, drawerView, catchFiltersMonths, catchFiltersSpecies } =
    exploreQueryParams;
  const catchesFilterMonthArray = getMonthArrayFromUrl(catchFiltersMonths);
  const catchesFilterSpeciesIdArray = getSpeciesIdArrayFromUrl(catchFiltersSpecies);

  const { location: userLocation } = useGeolocation();
  const userCoordinates = userLocation?.coordinates ?? DEFAULT_COORDINATES;
  const mapCoordinates = {
    latitude: latitude ?? userCoordinates.latitude,
    longitude: longitude ?? userCoordinates.longitude,
  };
  const zoom = getInitialZoomLevel(exploreQueryParams);

  const mapRef = useRef<HTMLDivElement>(null);

  const { layers } = useMapLayers();

  useUpdateExploreUrlFromUserInteraction();

  const activeLayers = Object.entries(layers)
    .filter(([, { isWholeLayerVisible }]) => isWholeLayerVisible)
    .map(([layer]) => layer);

  const { error: mapError } = useNewMap(mapbox, mapRef, zoom, mapCoordinates, {
    fibLayers: activeLayers as FishbrainLayer[],
    catchFilters: { months: catchesFilterMonthArray, speciesIds: catchesFilterSpeciesIdArray },
    mapStyle: exploreQueryParams.mapStyle,
  });
  const [isActive, setIsActive] = useState(false);
  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();

  if (mapError === MapboxExpectedError.WebGLDisabled) {
    return (
      <div className="mx-auto my-6 max-w-screen-md">
        <ErrorBanner errorType="webGLDisabled" errors={[WEB_GL_DISABLED_WARNING_MESSAGE]} />
      </div>
    );
  }

  return (
    <div className="relative grid w-full grid-cols-1 grid-rows-1">
      {(drawerView === 'regulation' || drawerView === 'regulations') && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-mid -translate-x-2/4 -translate-y-2/4">
          <NextImage unoptimized src={crosshair} alt="crosshair" width={204} height={204} />
        </div>
      )}
      <div className="col-start-1 col-end-1 row-start-1 row-end-1 w-full" ref={mapRef} />
      <div className="z-high col-start-1 col-end-1 row-start-1 row-end-1 m-auto ml-0 mt-0 h-10 md:ml-4 md:mt-4">
        {isTabletScreenWidthOrThinner ? (
          <MobileSearch setIsActive={setIsActive} isActive={isActive}>
            <SearchBoxMapContext
              placeholder="Search for a fishing water or location..."
              parent="map"
              isActive={isActive}
              setIsActive={setIsActive}
              isMobile
            />
          </MobileSearch>
        ) : (
          <SearchBoxMapContext
            placeholder="Search for a fishing water or location..."
            parent="map"
            isActive={isActive}
            setIsActive={setIsActive}
          />
        )}
      </div>
      {[FISHING_WATERS_LAYER, EMPTY_FISHING_WATERS_LAYER, FORBIDDEN_FISHING_WATERS_LAYER].map(
        fishingWaterLayer => (
          <MapIconClickHandler
            key={fishingWaterLayer.id}
            layer={fishingWaterLayer}
            handleMapIconClick={handleWaterIconClick}
            highlightLayer="fishingWaters"
          />
        ),
      )}
      <MapIconClickHandler
        layer={CATCH_POSITIONS_LAYER}
        handleMapIconClick={handleCatchIconClick}
        highlightLayer="catchPositions"
      />
      {Object.values(POINTS_OF_INTEREST_LAYERS).map(poiIconLayer => (
        <MapIconClickHandler
          key={poiIconLayer.id}
          layer={poiIconLayer}
          handleMapIconClick={handlePointOfInterestClick}
          highlightLayer="pointsOfInterest"
        />
      ))}
      <MapIconClickHandler
        layer={ARTIFICIAL_REEFS_LAYER}
        handleMapIconClick={handleArtificialReefClick}
        highlightLayer="artificialReefs"
      />
      {Object.values(WAYPOINTS_LAYERS).map(waypointLayer => (
        <MapIconClickHandler
          key={waypointLayer.id}
          layer={waypointLayer}
          handleMapIconClick={handleWaypointIconClick}
          highlightLayer="waypoints"
        />
      ))}
      <ZoomNotification isPremium={isPremium} initialZoom={zoom} />
    </div>
  );
};

const MainMapWrapper = () => (
  <MapboxErrorBoundary>
    <MainMap />
  </MapboxErrorBoundary>
);

// Default export to allow for the wrapper

export default MainMapWrapper;
